import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, FileText } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { BlueprintGrid } from "../components/BlueprintGrid";
import { fadeUp, viewportOnce, stagger } from "../lib/motion";

const REPORT_LABEL =
    "ILLUSTRATIVE DEMONSTRATION. FIGURES AND QUANTITIES ARE NOT VERIFIED AGAINST A LIVE ARCHITECTURAL PROJECT.";

const OVERVIEW = [
    { k: "PROJECT", v: "Illustrative home — Durban, KwaZulu-Natal" },
    { k: "TYPE", v: "Double-storey residence" },
    { k: "BEDROOMS", v: "3 + en-suite" },
    { k: "APPROX. FOOTPRINT", v: "286 m² under roof" },
    { k: "DOCUMENTS SUPPLIED", v: "2 × plans, 1 × schedule" },
    { k: "ESTIMATE BASIS", v: "Plans + regional pricing" },
];

const BOQ_ROWS = [
    ["01", "Preliminaries & general", "item", "1", "R48,500"],
    ["02", "Earthworks & foundations", "m³", "68", "R96,400"],
    ["03", "Masonry & brickwork", "m²", "214", "R187,300"],
    ["04", "Roof structure & coverings", "m²", "186", "R142,750"],
    ["05", "Windows & external doors", "no.", "14", "R118,900"],
    ["06", "Internal finishes", "item", "1", "R268,400"],
    ["07", "Electrical & plumbing", "item", "1", "R154,200"],
    ["08", "External works allowance", "item", "1", "R89,600"],
];

const MATERIALS = [
    { item: "50kg cement", spec: "CEM II 32.5N", source: "Regional benchmark", geo: "Durban, KZN", date: "Illustrative date" },
    { item: "Face brick", spec: "Clay stock brick", source: "Regional benchmark", geo: "Durban, KZN", date: "Illustrative date" },
    { item: "Roof timber", spec: "SA pine, treated", source: "Regional benchmark", geo: "Durban, KZN", date: "Illustrative date" },
];

const QUOTE_NOTES = [
    { h: "Scope difference identified", p: "Quotation excludes boundary wall allowance included in another submission — flagged for comparison." },
    { h: "Specification clarification", p: "Window specification described as 'aluminium' without brand range or glazing detail — item requires clarification." },
    { h: "Within observed range", p: "Masonry rates broadly consistent with the structured quantities derived from the plans." },
];

const RISKS = [
    { h: "Structural drawings not supplied", p: "Quantities for structural elements are estimated. Requires verification by an appropriate professional." },
    { h: "Ground conditions unknown", p: "No geotechnical information supplied. Excavation and foundation allowances are provisional." },
    { h: "Finish schedule incomplete", p: "Internal finish allowances used where no schedule was supplied — largest driver of estimate range." },
];

const ASSUMPTIONS = [
    "Estimate range assumes standard municipal services connections.",
    "Site assumed reasonably level; no retaining structures included.",
    "Regional pricing used where verified local data is unavailable.",
    "Excludes VAT, professional fees, municipal charges, pools and landscaping.",
];

const Row = ({ label, children }) => (
    <div className="border-t border-white/10 py-5">
        <p className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-mist">
            <span className="h-1.5 w-1.5 bg-accent" />
            {label}
        </p>
        <div className="mt-5">{children}</div>
    </div>
);

const SampleReportPage = () => (
    <div className="min-h-screen bg-ink text-white">
        <Seo
            title="Sample BuildEase Report — See What Construction Clarity Looks Like"
            description="An illustrative BuildEase report: project overview, BOQ summary, cost estimate, quotation observations, assumptions and a risk register for a fictional Durban home."
        />
        <Navbar />
        <main>
            <section className="relative overflow-hidden bg-ink pt-[128px] lg:pt-[168px]">
                <BlueprintGrid />
                <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
                    <motion.div variants={fadeUp} initial="hidden" animate="show">
                        <p className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-mist">
                            <span className="h-1.5 w-1.5 bg-accent" />
                            SAMPLE BUILDEASE REPORT
                        </p>
                        <h1
                            data-testid="sample-report-headline"
                            className="mt-8 max-w-4xl font-display text-4xl font-semibold leading-[1.08] text-white md:text-6xl"
                        >
                            See what greater construction clarity looks like
                            <span className="text-accent">.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-mist">
                            The report below shows how BuildEase organises a
                            residential project into structured, labelled
                            information. It is an illustrative demonstration —
                            every project's report depends on the plans and
                            information supplied.
                        </p>
                        <p
                            data-testid="report-demo-label"
                            className="mt-8 inline-block border border-accent/50 bg-accent/[0.06] px-4 py-2.5 font-mono text-[13px] leading-relaxed tracking-[0.18em] text-accent"
                        >
                            {REPORT_LABEL}
                        </p>
                    </motion.div>
                </div>
            </section>

            <section data-testid="report-body" className="relative py-16 lg:py-24">
                <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16">
                    <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
                        <motion.div variants={fadeUp}>
                            <Row label="PROJECT OVERVIEW">
                                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="report-overview">
                                    {OVERVIEW.map((o) => (
                                        <div key={o.k} className="border border-white/12 bg-surface/80 px-5 py-4">
                                            <p className="font-mono text-[13px] tracking-[0.22em] text-cad">{o.k}</p>
                                            <p className="mt-2 text-[15px] text-white/90">{o.v}</p>
                                        </div>
                                    ))}
                                </div>
                            </Row>

                            <Row label="ARCHITECTURAL INFORMATION SUMMARY">
                                <div className="border border-white/12 bg-surface/80 px-5 py-5 text-[15px] leading-relaxed text-mist">
                                    <p>
                                        3 bedrooms (2 en-suite), open-plan living
                                        and kitchen, double garage, 2 levels.
                                        Room dimensions and levels extracted
                                        from the supplied plans and organised
                                        for review.
                                    </p>
                                    <p className="mt-3 border-l-2 border-accent/60 pl-4 text-white/85">
                                        Flagged: structural drawings were not
                                        supplied — structural quantities are
                                        estimated and require professional
                                        verification.
                                    </p>
                                </div>
                            </Row>

                            <Row label="BOQ SUMMARY — MAJOR SECTIONS">
                                <div className="overflow-x-auto border border-white/12 bg-surface/80">
                                    <table className="w-full min-w-[640px] text-left" data-testid="report-boq">
                                        <thead>
                                            <tr className="border-b border-white/10 font-mono text-[13px] tracking-[0.2em] text-cad">
                                                <th className="px-5 py-3.5 font-medium">SEC.</th>
                                                <th className="px-5 py-3.5 font-medium">DESCRIPTION</th>
                                                <th className="px-5 py-3.5 font-medium">UNIT</th>
                                                <th className="px-5 py-3.5 font-medium">QTY</th>
                                                <th className="px-5 py-3.5 text-right font-medium">AMOUNT</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {BOQ_ROWS.map((r) => (
                                                <tr key={r[0]} className="border-b border-white/[0.06] text-[14px]">
                                                    <td className="px-5 py-3 font-mono text-[13px] text-cad">{r[0]}</td>
                                                    <td className="px-5 py-3 text-white/90">{r[1]}</td>
                                                    <td className="px-5 py-3 font-mono text-[13px] text-mist">{r[2]}</td>
                                                    <td className="px-5 py-3 font-mono text-[13px] text-mist">{r[3]}</td>
                                                    <td className="px-5 py-3 text-right font-mono text-[13px] text-white/90">{r[4]}</td>
                                                </tr>
                                            ))}
                                            <tr className="bg-white/[0.03] font-mono text-[13px]">
                                                <td className="px-5 py-3.5" colSpan="4">SUBTOTAL — MAJOR SECTIONS</td>
                                                <td className="px-5 py-3.5 text-right text-white">R1,106,050</td>
                                            </tr>
                                            <tr className="font-mono text-[13px]">
                                                <td className="px-5 py-3.5" colSpan="4">CONTINGENCY &amp; PROVISIONAL ALLOWANCES (7.5%)</td>
                                                <td className="px-5 py-3.5 text-right text-white/80">R82,954</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <p className="mt-4 font-mono text-[13px] leading-relaxed tracking-[0.16em] text-cad">
                                    EXCLUDES VAT, PROFESSIONAL FEES, MUNICIPAL
                                    CHARGES, POOLS AND LANDSCAPING. FULL BOQ
                                    INCLUDES MEASUREMENT DETAIL PER SECTION.
                                </p>
                            </Row>

                            <Row label="COST ESTIMATE">
                                <div className="border border-accent/40 bg-accent/[0.05] px-6 py-6">
                                    <p className="font-mono text-[13px] tracking-[0.22em] text-mist">
                                        ESTIMATED BUILD COST RANGE
                                    </p>
                                    <p className="mt-2 font-display text-3xl font-semibold text-white md:text-4xl">
                                        R2.1M — R2.6M
                                    </p>
                                    <p className="mt-3 max-w-xl text-[14px] leading-relaxed text-mist">
                                        Labelled <span className="text-white/90">Estimated</span> —
                                        derived from the supplied plans,
                                        structured quantities and regional
                                        pricing. The range reflects incomplete
                                        finish information and unverified site
                                        conditions. Not a quantity surveyor's
                                        formal estimate or a contractor's
                                        binding quotation.
                                    </p>
                                </div>
                            </Row>

                            <Row label="MATERIAL BREAKDOWN — LOCATION-AWARE">
                                <div className="grid gap-4 md:grid-cols-3" data-testid="report-materials">
                                    {MATERIALS.map((m) => (
                                        <div key={m.item} className="border border-white/12 bg-surface/80 px-5 py-4">
                                            <p className="text-[15px] font-medium text-white/90">{m.item}</p>
                                            <dl className="mt-3 space-y-1.5 font-mono text-[13px] text-mist">
                                                <p>SPEC · {m.spec}</p>
                                                <p>SOURCE · {m.source}</p>
                                                <p>GEO · {m.geo}</p>
                                                <p>DATE · {m.date}</p>
                                            </dl>
                                        </div>
                                    ))}
                                </div>
                                <p className="mt-4 font-mono text-[13px] leading-relaxed tracking-[0.16em] text-cad">
                                    PRICING PRIORITISES LOCAL SUPPLIERS, THEN
                                    CITY, PROVINCE AND REGION. NO UNVERIFIED
                                    PRICE IS DISPLAYED.
                                </p>
                            </Row>

                            <Row label="CONTRACTOR QUOTATION OBSERVATIONS">
                                <div className="grid gap-4 md:grid-cols-3" data-testid="report-quotes">
                                    {QUOTE_NOTES.map((q) => (
                                        <div key={q.h} className="border border-white/12 bg-surface/80 px-5 py-4">
                                            <p className="text-[15px] font-medium text-white/90">{q.h}</p>
                                            <p className="mt-2.5 text-[14px] leading-relaxed text-mist">{q.p}</p>
                                        </div>
                                    ))}
                                </div>
                                <p className="mt-4 font-mono text-[13px] leading-relaxed tracking-[0.16em] text-cad">
                                    NEUTRAL ASSESSMENTS — NOT A JUDGEMENT OF
                                    ANY CONTRACTOR.
                                </p>
                            </Row>

                            <Row label="RISK & UNCERTAINTY REGISTER">
                                <div className="grid gap-4 md:grid-cols-3" data-testid="report-risks">
                                    {RISKS.map((r) => (
                                        <div key={r.h} className="border border-white/12 bg-surface/80 px-5 py-4">
                                            <p className="text-[15px] font-medium text-white/90">{r.h}</p>
                                            <p className="mt-2.5 text-[14px] leading-relaxed text-mist">{r.p}</p>
                                        </div>
                                    ))}
                                </div>
                            </Row>

                            <Row label="ASSUMPTIONS & EXCLUSIONS">
                                <ul className="grid gap-2.5 md:grid-cols-2" data-testid="report-assumptions">
                                    {ASSUMPTIONS.map((a) => (
                                        <li key={a} className="flex items-start gap-2.5 text-[15px] leading-relaxed text-mist">
                                            <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-accent" />
                                            {a}
                                        </li>
                                    ))}
                                </ul>
                            </Row>

                            <Row label="OUTSTANDING INFORMATION">
                                <div className="border border-white/12 bg-surface/80 px-5 py-5 text-[15px] leading-relaxed text-mist">
                                    Structural drawings · geotechnical report ·
                                    finish schedule · municipal
                                    services confirmation. BuildEase flags
                                    outstanding information rather than
                                    filling gaps with invention.
                                </div>
                            </Row>
                        </motion.div>

                        <div className="mt-16 flex flex-wrap items-center gap-4 border-t border-white/10 pt-10">
                            <Link
                                to="/join-the-pilot"
                                data-testid="sample-report-get-started"
                                className="group inline-flex items-center gap-2 bg-accent px-7 py-4 text-[16px] font-semibold text-white transition-colors duration-300 hover:bg-[#ff7026]"
                            >
                                Get Started
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                            </Link>
                            <Link
                                to="/pricing"
                                data-testid="sample-report-pricing-link"
                                className="inline-flex items-center gap-2 border border-white/25 px-7 py-4 text-[16px] font-medium text-white transition-colors duration-300 hover:border-accent"
                            >
                                <FileText className="h-4 w-4" />
                                View Pricing
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </main>
        <Footer />
    </div>
);

export default SampleReportPage;
